import React, { useMemo, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Typography,
  TablePagination,
  TableSortLabel,
  Box,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Task } from "../../../../../models/TaskTracker/Task";
import {
  CustomMenu,
  CustomMenuItem,
  StyledTableCell,
} from "../../../../../styles/Shipmentstyles";
import EditIcon from "@mui/icons-material/Edit";
import ChatIcon from "@mui/icons-material/Chat";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import "../../../../../styles/table.css";
interface TaskTableProps {
  tasks: Task[];
  maxHeight: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  sortBy: string;
  sortType: string;
  onSortChange: (column: string) => void;
  toggleChat: (task_id: number) => void;
}

const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  maxHeight,
  page_no,
  limit,
  total_count,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortType,
  onSortChange,
  toggleChat,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null);
  // const [menuPosition, setMenuPosition] = useState<{
  //   top: number;
  //   left: number;
  // } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const handleRequestSort = (property: string) => {
    onSortChange(property);
  };
  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === "ASC" ? "↑" : "↓";
    }
    return "↑↓";
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    taskId: number
  ) => {
    setAnchorEl(event.currentTarget);
    // setMenuPosition({ top: event.clientY, left: event.clientX });
    setCurrentTaskId(taskId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentTaskId(null);
    // setMenuPosition(null);
  };

  const handleEditClick = () => {
    const id = currentTaskId;
    handleMenuClose();
    if (id != null) {
      navigate(`/task-tracker/add-edit-task?task-id=${id}`);
    }
  };

  const handleTaskClick = (taskId: number, taskTitle: string) => {
    navigate(
      `/task-tracker/add-edit-task?task-id=${taskId}&task-name=${encodeURIComponent(
        taskTitle
      )}`
    );
  };

  const colorMap: { [key: string]: string } = {
    A: "#0052cc",
    B: "#FF9800",
    C: "#03A9F4",
    D: "#9C27B0",
    E: "#F44336",
    F: "#673AB7",
    G: "#00BCD4",
    H: "#673AB7",
    I: "#795548",
    J: "#607D8B",
    K: "#3F51B5",
    L: "#E91E63",
    M: "#FF9800",
    N: "#0052cc",
    O: "#03A9F4",
    P: "#FF5722",
    Q: "#CDDC39",
    R: "#3F51B5",
    S: "#F44336",
    T: "#9C27B0",
    U: "#00BCD4",
    V: "#4CAF50",
    W: "#03A9F4",
    X: "#FF9800",
    Y: "#607D8B",
    Z: "#FFEB3B",
  };

  const assigneeColors = useMemo(() => {
    const map: { [key: string]: string } = {};
    tasks.forEach((task) => {
      task.assignee?.forEach((assignee) => {
        const firstLetter = assignee.name!.charAt(0).toUpperCase();
        const key = `${assignee.name}-${task.id}`;
        if (!map[key]) {
          map[key] = colorMap[firstLetter] || "#000";
        }
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  return (
    <>
      <Paper
        sx={{
          marginTop: "1rem",
          padding: "1.5rem",
          overflow: "hidden",
          borderRadius: "4px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        <TableContainer
          sx={{ maxHeight: maxHeight }}
          className="table-container"
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    padding: "6px 4px",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "14px",
                    maxWidth: "40px",
                  }}
                >
                  Task Id
                </TableCell>
                <TableCell
                  sx={{
                    padding: "6px 50px",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "14px",
                  }}
                >
                  Title
                </TableCell>
                <TableCell
                  sx={{
                    padding: "6px 12px",
                    textAlign: "center",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "14px",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    padding: "6px 12px",
                    textAlign: "center",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "14px",
                  }}
                >
                  Order Id
                </TableCell>
                <TableCell
                  sx={{
                    padding: "6px 12px",
                    textAlign: "center",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    zIndex: 10,
                    fontSize: "14px",
                  }}
                >
                  Assignees
                </TableCell>
                <TableCell
                  sx={{
                    padding: "6px 12px",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "14px",
                    textAlign: "center",
                  }}
                >
                  Reporter
                </TableCell>
                <StyledTableCell
                  sx={{
                    padding: "6px 12px",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "16px",
                    textAlign: "center",
                  }}
                >
                  <TableSortLabel
                    active={sortBy === "due_date"}
                    direction={sortType.toLowerCase() as "asc" | "desc"}
                    onClick={() => handleRequestSort("due_date")}
                    IconComponent={() => (
                      <span>{renderSortIcon("due_date")}</span>
                    )}
                    sx={{
                      color: "#B0ADCA",
                      "&.Mui-active": {
                        color: "#B0ADCA",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#B0ADCA !important",
                      },
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  >
                    Due Date
                  </TableSortLabel>
                </StyledTableCell>

                <StyledTableCell
                  sx={{
                    padding: "6px 12px",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "16px",
                    textAlign: "center",
                  }}
                >
                  <TableSortLabel
                    active={sortBy === "created_date"}
                    direction={sortType.toLowerCase() as "asc" | "desc"}
                    onClick={() => handleRequestSort("created_date")}
                    IconComponent={() => (
                      <span>{renderSortIcon("created_date")}</span>
                    )}
                    sx={{
                      color: "#B0ADCA",
                      "&.Mui-active": {
                        color: "#B0ADCA",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#B0ADCA !important",
                      },
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  >
                    Created Date
                  </TableSortLabel>
                </StyledTableCell>

                <StyledTableCell
                  sx={{
                    padding: "6px 12px",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "16px",
                    textAlign: "center",
                  }}
                >
                  <TableSortLabel
                    active={sortBy === "modified_date"}
                    direction={sortType.toLowerCase() as "asc" | "desc"}
                    onClick={() => handleRequestSort("modified_date")}
                    IconComponent={() => (
                      <span>{renderSortIcon("modified_date")}</span>
                    )}
                    sx={{
                      color: "#B0ADCA",
                      "&.Mui-active": {
                        color: "#B0ADCA",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "#B0ADCA !important",
                      },
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  >
                    Modified Date
                  </TableSortLabel>
                </StyledTableCell>

                <TableCell
                  sx={{
                    padding: "6px 12px",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "14px",
                    textAlign: "center",
                  }}
                >
                  Action
                </TableCell>
                <TableCell
                  sx={{
                    padding: "6px 12px",
                    fontWeight: 500,
                    color: "#B0ADCA",
                    fontSize: "14px",
                    textAlign: "center",
                  }}
                >
                  Chat
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks?.map((task, index) => (
                <TableRow
                  key={index}
                  // hover
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.palette.grey[200],
                    },
                    cursor: "pointer",
                  }}
                  onClick={() => handleTaskClick(task.id!, task.title!)}
                >
                  <TableCell
                    sx={{
                      padding: "8px",
                      fontSize: "12px",
                    }}
                  >
                    {task.id}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      minWidth: "8rem",
                      fontWeight: 500,
                      fontSize: "12px",
                      textAlign: "left",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: theme.palette.grey[100],
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {task.title}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      textAlign: "center",
                      fontSize: "12px",
                    }}
                  >
                    <Chip
                      label={
                        task.status === 1
                          ? "TO DO"
                          : task.status === 2
                          ? "IN PROGRESS"
                          : task.status === 3
                          ? "BLOCKED"
                          : "COMPLETED"
                      }
                      sx={{
                        backgroundColor:
                          task.status === 1
                            ? theme.palette.grey[500]
                            : task.status === 2
                            ? theme.palette.info.light
                            : task.status === 3
                            ? theme.palette.error.light
                            : theme.palette.success.light,
                        color: "#ffffff",
                        fontSize: "12px",
                        borderRadius: "4px",
                        height: "25px",
                        minWidth: "100px",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      maxWidth: "8rem",
                      fontWeight: 500,
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                  >
                    {task.order_id || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      minWidth: "150px",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        position: "relative",
                        height: 35,
                        left: "42%",
                      }}
                    >
                      {task?.assignee!?.slice(0, 3).map((assignee, idx) => (
                        <Chip
                          key={idx}
                          title={assignee.name}
                          label={assignee.name
                            ?.split(" ")
                            .map((namePart) => namePart.charAt(0).toUpperCase())
                            .join("")}
                          sx={{
                            backgroundColor:
                              assigneeColors[`${assignee.name}-${task.id}`],
                            color: "#fff",
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            fontSize: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            position: "absolute",
                            left: `${idx * 24}px`,
                            zIndex: task.assignee!.length + idx,
                            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                            border: "1px solid white",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "clip",
                            padding: 0,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              padding: 0,
                              lineHeight: "normal",
                            },
                            "&:hover": {
                              zIndex: task.assignee!.length + 1,
                              cursor: "default",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      fontWeight: 500,
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                  >
                    {task.reporter_name || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      fontWeight: 500,
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                  >
                    {formatDate(task.due_date)}
                  </TableCell>

                  <TableCell
                    sx={{
                      padding: "8px",
                      fontWeight: 500,
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                  >
                    {formatDate(task.created_date)}
                  </TableCell>

                  <TableCell
                    sx={{
                      padding: "8px",
                      fontWeight: 500,
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                  >
                    {formatDate(task.modified_date)}
                  </TableCell>

                  <TableCell
                    sx={{
                      padding: "8px",
                      fontSize: "12px",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMenuClick(event, task.id!);
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: "18px" }} />
                    </IconButton>
                  </TableCell>

                  <TableCell
                    sx={{
                      padding: "8px",
                      fontWeight: 500,
                      fontSize: "16px",
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleChat(task.id!);
                      }}
                    >
                      <ChatIcon sx={{ fontSize: "18px" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Single shared menu, driven by the row whose action button was clicked. */}
          <CustomMenu
            anchorEl={anchorEl}
            open={isOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <CustomMenuItem onClick={handleEditClick}>
              <EditIcon fontSize="small" style={{ marginRight: "4px" }} /> Edit
            </CustomMenuItem>
          </CustomMenu>
        </TableContainer>

        {tasks?.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="10rem"
            textAlign="center"
          >
            <Typography variant="h6" fontWeight="500" color="text.secondary">
              No data found
            </Typography>
          </Box>
        )}

        <TablePagination
          rowsPerPageOptions={[30, 50, 100, 150, 200]}
          component="div"
          count={total_count}
          page={page_no - 1}
          rowsPerPage={limit}
          onPageChange={(event, page_no) => onPageChange(event, page_no + 1)}
          onRowsPerPageChange={(e) =>
            handleRowsChange(parseInt(e.target.value, 10))
          }
          sx={{
            padding: "0.5rem",
          }}
        />
      </Paper>
    </>
  );
};

export default TaskTable;
